import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import FilterSidebar from "./components/FilterSidebar";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  filterMovies,
  genreOptions,
  getByType,
  regionOptions,
  typeLabel,
  type FilterState,
} from "@/lib/movies";
import type { MovieType } from "@/types/movie";

const PAGE_SIZE = 9;

const emptyFilter: FilterState = { genres: [], regions: [], years: [], sort: "latest" };

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const validType: MovieType | null =
    type === "movie" || type === "tv" ? (type as MovieType) : null;

  const [filter, setFilter] = useState<FilterState>(emptyFilter);
  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);

  const label = validType ? typeLabel(validType) : "";

  useDocumentMeta({
    title: validType
      ? `天天影院 - ${label}分类 - 最新${label}电视剧资源`
      : "天天影院 - 影视分类",
    description: validType
      ? `天天影院${label}分类页，汇集最新热门${label}作品，支持按类型、地区、年份多维筛选与排序，帮助你快速找到想看的${label}。`
      : "天天影院影视分类页。",
    keywords: validType ? `天天影院,${label},${label}推荐,最新${label},影视分类` : "天天影院,影视分类",
  });

  useEffect(() => {
    setFilter(emptyFilter);
    setPage(1);
  }, [validType]);

  const baseList = useMemo(() => (validType ? getByType(validType) : []), [validType]);
  const filtered = useMemo(() => filterMovies(baseList, filter), [baseList, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleToggle = (group: "genres" | "regions" | "years", value: string | number) => {
    setPage(1);
    setFilter((prev) => {
      const arr = prev[group] as (string | number)[];
      const exists = arr.includes(value);
      const next = exists ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, [group]: next } as FilterState;
    });
  };

  const handleReset = () => {
    setFilter(emptyFilter);
    setPage(1);
  };

  if (!validType) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
        <p className="mt-4 text-foreground-600">未找到该分类，请从导航进入电影或电视剧分类。</p>
      </div>
    );
  }

  const sidebar = (
    <FilterSidebar
      filter={filter}
      genreOptions={genreOptions}
      regionOptions={regionOptions}
      yearOptions={[2026, 2025]}
      onToggle={handleToggle}
      onSort={(sort) => {
        setPage(1);
        setFilter((prev) => ({ ...prev, sort }));
      }}
      onReset={handleReset}
    />
  );

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8 animate-fade-in">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label }]} />

      <div className="mt-5 rounded-lg bg-primary-950 px-6 py-7 md:px-8 md:py-9">
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">
          {label}
        </h1>
        <p className="mt-2 text-sm md:text-[15px] text-background-200/90 max-w-2xl">
          天天影院{label}频道收录 {baseList.length} 部精选作品，涵盖{" "}
          {Array.from(new Set(baseList.flatMap((m) => m.genres))).slice(0, 6).join("、")} 等多种类型，
          支持多维筛选，助你快速发现高分好片。
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
        <aside className="hidden lg:block">{sidebar}</aside>

        <section>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-sm text-foreground-500">
              共 <span className="text-primary-600 font-semibold">{filtered.length}</span> 部作品
            </p>
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700 cursor-pointer"
            >
              <i className="ri-filter-3-line" />
              筛选
            </button>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
                {pageItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} showActors />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2 flex-wrap">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setPage(currentPage - 1)}
                    className="px-3 py-2 rounded-md border border-background-300 text-sm text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-s-line" /> 上一页
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPage(i + 1)}
                      className={`w-9 h-9 rounded-md text-sm transition-colors cursor-pointer ${
                        i + 1 === currentPage
                          ? "bg-primary-500 text-background-50 font-medium"
                          : "border border-background-300 text-foreground-700 hover:border-primary-400"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => setPage(currentPage + 1)}
                    className="px-3 py-2 rounded-md border border-background-300 text-sm text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    下一页 <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-background-200 bg-background-50 py-16 text-center">
              <i className="ri-inbox-2-line text-5xl text-foreground-300" />
              <p className="mt-4 text-foreground-700">
                抱歉，没有找到符合筛选条件的影视作品
              </p>
              <button
                type="button"
                onClick={handleReset}
                className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium px-5 py-2.5 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line" />
                重置筛选条件
              </button>
            </div>
          )}
        </section>
      </div>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-[70] lg:hidden flex items-end md:items-center justify-center bg-foreground-950/60 animate-fade-in"
          onClick={() => setMobileOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full md:max-w-md max-h-[80vh] overflow-y-auto rounded-t-lg md:rounded-lg bg-background-50 p-4 animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-heading font-bold text-foreground-950">筛选与排序</span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
                aria-label="关闭筛选"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            {sidebar}
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="mt-4 w-full rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium py-3 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}